<template>
  <b-card title="Свежие релизы">
    <div ref="albumsContainer">
      <b-tabs pills>
        <!-- Tab: Spotify -->
        <b-tab @click="loadSpotifyReleases">
          <template #title>
            <feather-icon icon="MusicIcon" size="16" class="mr-0 mr-sm-50" />
            <span class="d-none d-sm-inline">Спотифай</span>
          </template>

          <div class="my-1 py-25">
            <div v-if="isLoadingSpotify" class="text-muted">
              <b-spinner small class="mr-50" /> Загрузка...
            </div>
            <div v-if="spotifyError" class="text-danger mb-1">{{ spotifyError }}</div>

            <div v-if="spotifyReleases.length">
              <template v-for="(row, rowIndex) in albumRows(spotifyReleases)">
                <div :key="'rel-sp-row-' + rowIndex" class="d-flex flex-wrap mb-1" style="gap: 1rem;">
                  <album-card
                      v-for="album in row"
                      :key="'sp-' + album.id"
                      :album="album"
                      source="spotify"
                      :show-add-cover="false"
                      :active="!!activeAlbum && activeAlbum.source === 'spotify' && activeAlbum.id === album.id"
                      @toggle="toggleAlbumTracks"
                  />
                </div>
                <album-tracks-panel
                    v-if="rowHasActiveAlbum(row, 'spotify')"
                    :key="'rel-sp-panel-' + rowIndex"
                    :album-name="activeAlbum.name"
                    :artists="activeAlbum.artistsList"
                    :source="activeAlbum.source"
                    :source-url="albumSourceUrl(activeAlbum)"
                    :tracks="albumTracks[activeAlbum.id] || null"
                    :loading="!!albumTracksLoading[activeAlbum.id]"
                    @close="closeAlbumTracks"
                    @add-track="addReleaseTrack"
                    @add-artist="addReleaseArtist"
                />
              </template>
            </div>
            <div v-else-if="spotifyLoaded && !isLoadingSpotify && !spotifyError" class="text-muted">
              Релизы не найдены
            </div>
          </div>
        </b-tab>

        <!-- Tab: Yandex -->
        <b-tab @click="loadYandexReleases">
          <template #title>
            <feather-icon icon="PlayCircleIcon" size="16" class="mr-0 mr-sm-50" />
            <span class="d-none d-sm-inline">Яндекс.Музыка</span>
          </template>

          <div class="my-1 py-25">
            <div v-if="isLoadingYandex" class="text-muted">
              <b-spinner small class="mr-50" /> Загрузка...
            </div>
            <div v-if="yandexError" class="text-danger mb-1">{{ yandexError }}</div>

            <div v-if="yandexReleases.length">
              <template v-for="(row, rowIndex) in albumRows(yandexReleases)">
                <div :key="'rel-ya-row-' + rowIndex" class="d-flex flex-wrap mb-1" style="gap: 1rem;">
                  <album-card
                      v-for="album in row"
                      :key="'ya-' + album.id"
                      :album="album"
                      source="yandex"
                      :show-add-cover="false"
                      :active="!!activeAlbum && activeAlbum.source === 'yandex' && activeAlbum.id === album.id"
                      @toggle="toggleAlbumTracks"
                  />
                </div>
                <album-tracks-panel
                    v-if="rowHasActiveAlbum(row, 'yandex')"
                    :key="'rel-ya-panel-' + rowIndex"
                    :album-name="activeAlbum.name"
                    :artists="activeAlbum.artistsList"
                    :source="activeAlbum.source"
                    :source-url="albumSourceUrl(activeAlbum)"
                    :tracks="albumTracks[activeAlbum.id] || null"
                    :loading="!!albumTracksLoading[activeAlbum.id]"
                    @close="closeAlbumTracks"
                    @add-track="addReleaseTrack"
                    @add-artist="addReleaseArtist"
                />
              </template>
            </div>
            <div v-else-if="yandexLoaded && !isLoadingYandex && !yandexError" class="text-muted">
              Релизы не найдены
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </div>
  </b-card>
</template>

<script>
import { BCard, BTabs, BTab, BSpinner } from 'bootstrap-vue'
import { ref, onMounted, onUnmounted, nextTick, watch } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import musicStoreModule from '@/views/db-music/musicStoreModule'
import AlbumCard from '@/views/db-music/AlbumCard.vue'
import AlbumTracksPanel from '@/views/db-music/AlbumTracksPanel.vue'

export default {
  name: 'FreshReleases',
  components: {
    BCard,
    BTabs,
    BTab,
    BSpinner,
    AlbumCard,
    AlbumTracksPanel,
  },
  setup() {
    const STORE_MODULE_NAME = 'releases'
    const toast = useToast()

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const spotifyReleases = ref([])
    const isLoadingSpotify = ref(false)
    const spotifyError = ref('')
    const spotifyLoaded = ref(false)

    const yandexReleases = ref([])
    const isLoadingYandex = ref(false)
    const yandexError = ref('')
    const yandexLoaded = ref(false)

    // Один активный альбом одновременно: { source, id, name } либо null.
    const activeAlbum = ref(null)
    const albumTracks = ref({})
    const albumTracksLoading = ref({})
    const albumsContainer = ref(null)
    const albumColumns = ref(1)

    const loadSpotifyReleases = async () => {
      if (spotifyLoaded.value || isLoadingSpotify.value) return
      isLoadingSpotify.value = true
      spotifyError.value = ''
      try {
        const response = await store.dispatch('releases/fetchSpotifyNewReleases')
        spotifyReleases.value = response.data || []
        spotifyLoaded.value = true
      } catch (error) {
        spotifyError.value = 'Ошибка загрузки релизов Спотифай'
      } finally {
        isLoadingSpotify.value = false
      }
    }

    const loadYandexReleases = async () => {
      if (yandexLoaded.value || isLoadingYandex.value) return
      isLoadingYandex.value = true
      yandexError.value = ''
      try {
        const response = await store.dispatch('releases/fetchYandexNewReleases')
        yandexReleases.value = response.data || []
        yandexLoaded.value = true
      } catch (error) {
        yandexError.value = 'Ошибка загрузки релизов Яндекс.Музыки'
      } finally {
        isLoadingYandex.value = false
      }
    }

    const loadAlbumTracks = async (album, source) => {
      if (albumTracks.value[album.id]) return
      if (albumTracksLoading.value[album.id]) return
      albumTracksLoading.value = { ...albumTracksLoading.value, [album.id]: true }
      try {
        const response = await store.dispatch('releases/fetchReleasesAlbumTracks', {
          source,
          albumId: album.id,
        })
        // adding — реактивный флаг блокировки кнопки добавления (Vue 2: свойство
        // должно существовать заранее, иначе мутация не отслеживается).
        const tracks = (response.data || []).map(track => ({ ...track, adding: false }))
        albumTracks.value = { ...albumTracks.value, [album.id]: tracks }
      } catch (error) {
        const next = { ...albumTracks.value }
        delete next[album.id]
        albumTracks.value = next
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка загрузки треков альбома',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        albumTracksLoading.value = { ...albumTracksLoading.value, [album.id]: false }
      }
    }

    // Пересчитать число колонок по фактической ширине контейнера сетки.
    // Карточка 220px + gap 1rem (16px). Игнорируем нулевую ширину (скрытая вкладка).
    const measureAlbumColumns = () => {
      const el = albumsContainer.value
      if (!el || !el.clientWidth) return
      const cardWidth = 220
      const gap = 16
      albumColumns.value = Math.max(1, Math.floor((el.clientWidth + gap) / (cardWidth + gap)))
    }

    // Разбить список альбомов на строки по числу колонок — панель треков
    // вставляется под строкой с активным альбомом (карточки во flex-wrap).
    const albumRows = (albums) => {
      const size = albumColumns.value
      const rows = []
      for (let i = 0; i < albums.length; i += size) {
        rows.push(albums.slice(i, i + size))
      }
      return rows
    }

    const rowHasActiveAlbum = (row, source) => {
      const active = activeAlbum.value
      return !!active && active.source === source && row.some(album => album.id === active.id)
    }

    const toggleAlbumTracks = (album, source) => {
      const isActive = activeAlbum.value
          && activeAlbum.value.source === source
          && activeAlbum.value.id === album.id
      if (isActive) {
        activeAlbum.value = null
        return
      }
      measureAlbumColumns()
      activeAlbum.value = {
        source,
        id: album.id,
        name: album.name,
        // Те же объекты артистов, что и на карточке: добавление артиста
        // обновит и шапку панели, и карточку.
        artistsList: album.artists_list || null,
      }
      loadAlbumTracks(album, source)
    }

    // Ссылка на страницу альбома в источнике.
    const albumSourceUrl = (album) => (album.source === 'yandex'
      ? `https://music.yandex.ru/album/${album.id}`
      : `https://open.spotify.com/album/${album.id}`)

    const closeAlbumTracks = () => {
      activeAlbum.value = null
    }

    let albumsResizeObserver = null
    onMounted(() => {
      if (typeof ResizeObserver !== 'undefined' && albumsContainer.value) {
        albumsResizeObserver = new ResizeObserver(() => measureAlbumColumns())
        albumsResizeObserver.observe(albumsContainer.value)
      } else {
        window.addEventListener('resize', measureAlbumColumns)
      }
    })
    onUnmounted(() => {
      if (albumsResizeObserver) {
        albumsResizeObserver.disconnect()
        albumsResizeObserver = null
      } else {
        window.removeEventListener('resize', measureAlbumColumns)
      }
    })

    // При появлении списков пересчитать колонки после отрисовки,
    // иначе первая отрисовка идёт со стартовым albumColumns = 1.
    watch([spotifyReleases, yandexReleases], () => {
      nextTick(() => measureAlbumColumns())
    })

    // Первый артист в БД — трек привязывается к нему; иначе сначала
    // создаётся артист (паттерн PlaylistView).
    const addReleaseTrack = async (track) => {
      if (track.adding) return
      const firstArtist = Array.isArray(track.artists) ? track.artists[0] : null
      if (!firstArtist || !firstArtist.name) return
      track.adding = true
      try {
        let artistId = firstArtist.id
        if (!artistId) {
          const response = await store.dispatch('releases/createArtist', { title: firstArtist.name })
          artistId = response.data.data.id
          firstArtist.id = artistId
        }
        await store.dispatch('releases/addTrack', { id: artistId, queryParams: { title: track.title } })
        track.in_db = true
        toast({
          component: ToastificationContent,
          props: {
            title: 'Трек добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка добавления трека',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      } finally {
        track.adding = false
      }
    }

    const addReleaseArtist = async (artist) => {
      try {
        const response = await store.dispatch('releases/createArtist', { title: artist.name })
        artist.id = response.data.data.id
        toast({
          component: ToastificationContent,
          props: {
            title: 'Артист добавлен',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: 'Ошибка добавления артиста',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      }
    }

    // Первая вкладка (Спотифай) видна сразу — грузим при открытии страницы.
    loadSpotifyReleases()

    return {
      spotifyReleases,
      isLoadingSpotify,
      spotifyError,
      spotifyLoaded,
      yandexReleases,
      isLoadingYandex,
      yandexError,
      yandexLoaded,
      activeAlbum,
      albumTracks,
      albumTracksLoading,
      albumsContainer,
      loadSpotifyReleases,
      loadYandexReleases,
      albumRows,
      rowHasActiveAlbum,
      toggleAlbumTracks,
      closeAlbumTracks,
      albumSourceUrl,
      addReleaseTrack,
      addReleaseArtist,
    }
  },
}
</script>
